import { useInfraSites } from '@/lib/api/queries';
import { useInfraStore } from '@/lib/store/infra';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dot } from '@/components/ui/dot';
import { formatAgo } from '@/lib/format';
import { cn } from '@/lib/utils';

export function InfraList() {
  const { data, isPending, isError } = useInfraSites();
  const heartbeats = useInfraStore((s) => s.byId);
  const filterStatus = useInfraStore((s) => s.filterStatus);
  const filterType = useInfraStore((s) => s.filterType);
  const select = useInfraStore((s) => s.select);
  const toggleStatus = useInfraStore((s) => s.toggleStatus);
  const toggleType = useInfraStore((s) => s.toggleType);

  const sites = (data ?? [])
    .map((s) => ({ ...s, hb: heartbeats[s.site_id] }))
    .filter((s) => filterStatus[s.status as 'active' | 'degraded' | 'inactive'] && filterType[s.type]);

  const active = sites.filter((s) => s.status === 'active').length;
  const degraded = sites.filter((s) => s.status === 'degraded').length;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <div className="text-[0.846em] font-bold">Infrastructure</div>
        <div className="text-[0.692em] text-(--color-text3) font-mono">
          {sites.length} sites · <span className="text-(--color-green)">{active} active</span> · <span className="text-(--color-amber)">{degraded} degraded</span>
        </div>
        <div className="flex gap-1 mt-2 flex-wrap">
          {(['active', 'degraded', 'inactive'] as const).map((k) => (
            <button key={k} onClick={() => toggleStatus(k)} className={chipClass(filterStatus[k])}>
              <Dot variant={k === 'active' ? 'ok' : k === 'degraded' ? 'warn' : 'crit'} size="xs" /> {k}
            </button>
          ))}
          <div className="w-px bg-(--color-border)" />
          {(['F', 'M'] as const).map((k) => (
            <button key={k} onClick={() => toggleType(k)} className={chipClass(filterType[k])}>
              {k === 'F' ? 'Fixed' : 'Mobile'}
            </button>
          ))}
        </div>
      </header>
      <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
        {isPending && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        {isError && <div className="text-[0.769em] text-(--color-red)">Failed to load infrastructure.</div>}
        {sites.map((s) => {
          const tone = s.status === 'active' ? 'ok' : s.status === 'degraded' ? 'warn' : 'crit';
          const totalDev = s.components.length;
          const offlineDev = s.components.filter((c) => !c.online).length;
          const backhaul = [
            s.backhaul_primary,
            s.backhaul_secondary && s.backhaul_secondary !== 'n/a' ? s.backhaul_secondary : null,
          ].filter(Boolean).join(' + ');
          const heartbeatAge = s.hb?.last_heartbeat
            ? (Date.now() - new Date(s.hb.last_heartbeat).getTime()) / 1000
            : null;
          // Mockup `drawInfraMarkers` colour ladder L11407 — green <5 s, amber
          // <1 h, red after that. Encodes heartbeat staleness as a chip.
          const hbTone = heartbeatAge == null
            ? 'muted' as const
            : heartbeatAge < 5 ? 'ok' as const
            : heartbeatAge < 3600 ? 'warn' as const
            : 'crit' as const;
          return (
            <article
              key={s.site_id}
              role="button"
              tabIndex={0}
              onClick={() => select(s.site_id)}
              className={cn(
                'rounded-md border border-(--color-border) bg-(--color-surface) p-2.5 cursor-pointer hover:border-(--color-green) hover:bg-(--color-green-bg) border-t-2 transition-colors',
                tone === 'ok' && 'border-t-(--color-green)',
                tone === 'warn' && 'border-t-(--color-amber)',
                tone === 'crit' && 'border-t-(--color-red)',
              )}
            >
              {/* Mockup `_infraCardHtml` L11395-L11407: header row + meta line
                  combining backhaul / device count / offline, optional truncated
                  description, footer with live "Updated" heartbeat indicator. */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="text-[0.846em] font-bold">{s.site_id}</span>
                  <Badge variant="muted">{s.type === 'F' ? 'Fixed' : 'Mobile'}</Badge>
                </div>
                <Badge variant={tone}>{s.status}</Badge>
              </div>
              <div className="text-[0.692em] text-(--color-text3) font-mono mt-0.5 truncate">{s.address}</div>
              {s.description && (
                <div className="text-[0.615em] text-(--color-text3) mt-0.5 truncate">{s.description}</div>
              )}
              <div className="text-[0.615em] font-mono text-(--color-text3) mt-1">
                {backhaul} · {totalDev} device{totalDev === 1 ? '' : 's'}
                {offlineDev > 0 && (
                  <span className="text-(--color-red) font-semibold"> · {offlineDev} offline</span>
                )}
              </div>
              <div className="flex items-center gap-1 text-[0.615em] font-mono mt-1">
                <Dot variant={hbTone} size="xs" />
                <span className="text-(--color-text3)">
                  Updated {s.hb?.last_heartbeat ? formatAgo(s.hb.last_heartbeat) : '—'}
                </span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

const chipClass = (on: boolean) =>
  cn(
    'px-2 h-6 rounded-full text-[0.615em] font-semibold uppercase tracking-wider border transition-colors flex items-center gap-1',
    on
      ? 'bg-(--color-selected) text-(--color-selected-fg) border-(--color-selected)'
      : 'bg-(--color-surface) border-(--color-border) text-(--color-text3) hover:border-(--color-green)',
  );
